import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { adminOrdersService, Order, OrderItem } from '../../services/adminOrders';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';
import { useOrdersRealtime, RealtimeOrder } from '../../hooks/useOrdersRealtime';

// Tipos
type OrderStatus = 'created' | 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled';

interface FrontendOrder {
  id: string;
  tableNumber: number;
  items: OrderItem[];
  totalAmount: number;
  status: OrderStatus;
  createdAt: Date;
  updatedAt: Date;
  estimatedTime: number;
  waiterRequested: boolean;
  specialNotes: string;
  waiterName: string;
  assignedWaiters: string[];
}

const statusLabels = {
  created: 'Creada',
  pending: 'Pendiente',
  preparing: 'Preparando',
  ready: 'Listo',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const statusColors = {
  created: '#A0AEC0',
  pending: '#4299E1',
  preparing: '#38A169',
  ready: '#22C55E',
  delivered: '#718096',
  cancelled: '#E53E3E',
};

// Componente de tarjeta de orden
const OrderCard = ({ 
  order, 
  isPriority, 
  isNextInQueue,
  onPress,
  getStatusColor,
  formatTime,
  formatCreationTime,
  formatPrice,
  canViewAmounts,
}: { 
  order: FrontendOrder; 
  isPriority: boolean; 
  isNextInQueue: boolean;
  onPress: () => void;
  getStatusColor: (status: OrderStatus) => string;
  formatTime: (date: Date) => string;
  formatCreationTime: (date: Date) => string;
  formatPrice: (price: number) => string;
  canViewAmounts: boolean;
}) => {
  const getOrderShortCode = (orderId: string) => {
    if (!orderId) return '';
    const parts = String(orderId).split('-');
    if (parts.length >= 2 && parts[1]) return String(parts[1]).toUpperCase();
    const s = String(orderId);
    return s.length >= 4 ? s.slice(-4).toUpperCase() : s.toUpperCase();
  };

  // Calcular si la orden es antigua para aplicar estilos adicionales
  const now = new Date();
  const orderAge = now.getTime() - order.createdAt.getTime();
  const minutesWaiting = Math.floor(orderAge / 60000);
  const isOldOrder = minutesWaiting > 15;
  const totalItems = order.items.reduce((acc, item) => acc + item.quantity, 0);

  // Mostrar versión simplificada para pendientes y preparando
  const isSimple = order.status === 'pending' || order.status === 'preparing';

  return (
    <div 
      className={`order-card ${isPriority ? 'priority' : ''} ${isNextInQueue ? 'next-in-queue' : ''} ${isOldOrder ? 'old-order' : ''}`}
      onClick={onPress}
    >
      <div 
        className="order-status-bar"
        style={{ backgroundColor: getStatusColor(order.status) }}
      />
      <div className="order-header">
        <div className="order-info">
          <div
            className="order-number"
            title={
              order.tableNumber === 0
                ? `${'#' + getOrderShortCode(order.id)}${order.specialNotes ? ` ${order.specialNotes}` : ''}`
                : `Mesa ${order.tableNumber}`
            }
          >
            {order.tableNumber === 0
              ? `${'#' + getOrderShortCode(order.id)}${order.specialNotes ? ` ${order.specialNotes}` : ''}`
              : `Mesa ${order.tableNumber}`}
          </div>
          <div className="order-time">{formatTime(order.createdAt)}</div>
        </div>
      </div>
      {isSimple ? (
        <div className="order-items-simple px-4 py-2">
          <div className="text-sm text-gray-600 mb-2">Items: <span className="font-semibold">{totalItems}</span></div>
          <div className="space-y-1">
              {order.items.slice(0, 2).map((item, index) => (
              <div key={index} className="flex items-center text-sm">
                <span className="text-gray-500 mr-2">x{item.quantity}</span>
                  <span className="text-gray-700">{item.name}</span>
                  {(item as any).size && (
                    <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{(item as any).size}</span>
                  )}
                  {(item as any).specialInstructions && (
                    <span className="ml-2 text-xs text-gray-500 italic truncate max-w-[160px]">
                      {(item as any).specialInstructions}
                    </span>
                  )}
              </div>
            ))}
            {order.items.length > 2 && (
              <div className="text-xs text-blue-600 font-medium">
                +{order.items.length - 2} items más...
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="order-items">
              {order.items.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-quantity">x{item.quantity}</div>
                  <div className="item-name">{item.name}
                    {(item as any).size && (
                      <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{(item as any).size}</span>
                    )}
                  </div>
              </div>
            ))}
          </div>
          {order.specialNotes && (
            <div className="order-notes">
              <div className="notes-label">Notas especiales:</div>
              <div className="notes-text">{order.specialNotes}</div>
            </div>
          )}
        </>
      )}
      <div className="order-footer">
        {/* Mostrar el total solo si la orden está lista o entregada Y el usuario puede ver montos */}
        {(order.status === 'ready' || order.status === 'delivered') && canViewAmounts && (
          <div className="order-total">
            <span className="total-label">Total:</span>
            <span className="total-amount">{formatPrice(order.totalAmount)}</span>
          </div>
        )}
        <div className="order-creation-time">
          {formatCreationTime(order.createdAt)}
        </div>
      </div>
      {isOldOrder && (
        <div className="old-order-warning">
          ⚠️ ORDEN ANTIGUA - {minutesWaiting} minutos de espera
        </div>
      )}
    </div>
  );
};

export default function KitchenPage() {
  const { user, isOwner, isManager, isKitchen } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [selectedOrder, setSelectedOrder] = useState<FrontendOrder | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string; onRetry?: () => void }>({ open: false, message: '' });
  const [confirmCancel, setConfirmCancel] = useState<{ open: boolean; orderId?: string }>(() => ({ open: false }));

  const getOrderShortCode = (orderId: string) => {
    if (!orderId) return '';
    const parts = String(orderId).split('-');
    if (parts.length >= 2 && parts[1]) return String(parts[1]).toUpperCase();
    const s = String(orderId);
    return s.length >= 4 ? s.slice(-4).toUpperCase() : s.toUpperCase();
  };
  
  // Determinar permisos del usuario
  const userIsOwner = isOwner();
  const userIsManager = isManager();
  const userIsKitchen = isKitchen();
  
  // Solo owner, manager y kitchen pueden acceder a cocina
  const userCanAccessKitchen = userIsOwner || userIsManager || userIsKitchen;
  
  // Solo owner y manager pueden ver montos
  const canViewAmounts = userIsOwner || userIsManager;

  // Hook en tiempo real para todas las órdenes
  const { orders: realtimeOrders, ordersByStatus, loading, setInitialOrders } = useOrdersRealtime();
  
  // Estado para órdenes cargadas inicialmente
  const [initialOrdersLoaded, setInitialOrdersLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Log para debug del estado del usuario
  console.log('🔍 Kitchen - Estado del usuario:', {
    hasUser: !!user,
    userId: user?.uid,
    userIsOwner,
    userIsManager,
    userIsKitchen,
    userCanAccessKitchen,
    canViewAmounts,
    selectedRestaurantId,
    waitingForUser
  });



  // Función para cargar órdenes iniciales
  const loadInitialOrders = useCallback(async (force: boolean = false) => {
    if (!user?.uid || !selectedRestaurantId || (!force && initialOrdersLoaded)) {
      return;
    }
    
    try {
      console.log('🔄 Cargando órdenes iniciales para restaurante:', selectedRestaurantId);
      
      const [createdOrders, pendingOrders, preparingOrders, readyOrders] = await Promise.all([
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'created'),
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'pending'),
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'preparing'),
        adminOrdersService.getOrdersByRestaurant(user.uid, selectedRestaurantId, 'ready'),
      ]);

      const uniqueOrdersById = new Map<string, Order>();
      [...createdOrders, ...pendingOrders, ...preparingOrders, ...readyOrders].forEach(order => {
        uniqueOrdersById.set(order.id, order);
      });

      const allOrders = Array.from(uniqueOrdersById.values());
      
      console.log('📊 Órdenes iniciales recibidas:', allOrders.length);
      
      // Convertir órdenes del backend al formato del frontend
      const convertedOrders: FrontendOrder[] = allOrders.map(convertBackendOrderToFrontend);
      
      // Convertir al formato de RealtimeOrder para el hook
      const realtimeOrders: RealtimeOrder[] = convertedOrders.map(order => ({
        id: order.id,
        tableNumber: order.tableNumber,
        items: order.items.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          specialInstructions: item.specialInstructions,
          price: item.price,
          size: item.size,
        })),
        totalAmount: order.totalAmount,
        status: order.status as OrderStatus,
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        notes: order.specialNotes,
        isPaid: false,
        paymentMethod: '',
        restaurantId: selectedRestaurantId,
        restaurantName: '',
        tableId: '',
        username: order.waiterName,
        waiterId: '',
      }));
      
      // Actualizar el hook con las órdenes iniciales
      setInitialOrders(realtimeOrders);
      setInitialOrdersLoaded(true);
      
      console.log('✅ Órdenes iniciales cargadas exitosamente');
    } catch (error: any) {
      console.error('❌ Error cargando órdenes iniciales:', error);
      const message = error instanceof ApiError && error.message ? error.message : (error?.message || 'No se pudieron cargar las órdenes');
      const title = error instanceof ApiError && error.status >= 400 && error.status < 500 ? 'No se pudieron cargar las órdenes' : 'Error del servidor';
      setErrorModal({ open: true, title, message, onRetry: () => loadInitialOrders(true) });
    }
  }, [user?.uid, selectedRestaurantId, initialOrdersLoaded, setInitialOrders]);

  const handleGlobalRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await loadInitialOrders(true);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadInitialOrders]);

  // Auto-refresh cada 30s (mismo comportamiento que el botón Refrescar)
  useEffect(() => {
    if (!userCanAccessKitchen || !selectedRestaurantId || !user?.uid) return;
    const id = window.setInterval(() => {
      if (isRefreshing) return;
      void handleGlobalRefresh();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [userCanAccessKitchen, selectedRestaurantId, user?.uid, isRefreshing, handleGlobalRefresh]);

  // Función para convertir orden del backend al formato del frontend
  const convertBackendOrderToFrontend = (backendOrder: Order): FrontendOrder => {
    console.log('🔄 Converting backend order:', backendOrder);
    console.log('🔄 Backend order items:', backendOrder.items);
    
    const convertedOrder = {
      id: backendOrder.id,
      tableNumber: parseInt(backendOrder.tableNumber) || 0,
      items: backendOrder.items?.map((item) => {
        console.log('🔄 Converting item:', item);
        return {
          id: item.id,
          name: item.name || 'Item sin nombre',
          quantity: item.quantity || 1,
          specialInstructions: item.specialInstructions || item.notes || (item as any).special_instructions,
          price: typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0),
          size: item.size || (item as any).variant || (item as any).tamano || (item as any).tamaño,
        };
      }) || [],
      totalAmount: typeof backendOrder.totalAmount === 'string' 
        ? parseFloat(backendOrder.totalAmount) 
        : (backendOrder.totalAmount || 0),
      status: String(backendOrder.status).toLowerCase() as OrderStatus,
      createdAt: new Date(backendOrder.createdAt),
      updatedAt: new Date(backendOrder.updatedAt),
      estimatedTime: backendOrder.estimatedTime || 15,
      waiterRequested: false, // No disponible en el servicio actual
      specialNotes: backendOrder.specialNotes || backendOrder.notes || '',
      waiterName: backendOrder.waiterName || 'Sin mesero',
      assignedWaiters: [], // No disponible en el servicio actual
    };
    
    console.log('✅ Converted order:', convertedOrder);
    return convertedOrder;
  };

  // Función para convertir orden en tiempo real al formato del frontend
  const convertRealtimeOrderToFrontend = (realtimeOrder: RealtimeOrder): FrontendOrder => {
    console.log('🔄 Converting realtime order:', realtimeOrder);
    
    const convertedOrder = {
      id: realtimeOrder.id,
      tableNumber: realtimeOrder.tableNumber,
      items: realtimeOrder.items?.map((item) => {
        console.log('🔄 Converting item:', item);
        return {
          id: item.id,
          name: item.name || 'Item sin nombre',
          quantity: item.quantity || 1,
          specialInstructions: item.specialInstructions || (item as any).notes || (item as any).special_instructions,
          price: typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0),
          size: item.size || (item as any).variant || (item as any).tamano || (item as any).tamaño,
        };
      }) || [],
      totalAmount: typeof realtimeOrder.totalAmount === 'string' 
        ? parseFloat(realtimeOrder.totalAmount) 
        : (realtimeOrder.totalAmount || 0),
      status: String(realtimeOrder.status).toLowerCase() as OrderStatus,
      createdAt: new Date(realtimeOrder.createdAt),
      updatedAt: new Date(realtimeOrder.updatedAt),
      estimatedTime: 15, // Valor por defecto
      waiterRequested: false, // No disponible en el servicio actual
      specialNotes: realtimeOrder.notes || '',
      waiterName: realtimeOrder.username || 'Sin mesero',
      assignedWaiters: [], // No disponible en el servicio actual
    };
    
    console.log('✅ Converted order:', convertedOrder);
    return convertedOrder as FrontendOrder;
  };

  // Tipografía adaptable para el modal (más grande cuando hay pocos items).
  const modalItemCount = selectedOrder?.items?.length || 0;
  const modalItemsTextClass =
    modalItemCount <= 3 ? 'text-lg' :
    modalItemCount <= 6 ? 'text-base' :
    'text-sm';
  const modalMetaTextClass =
    modalItemCount <= 3 ? 'text-base' :
    modalItemCount <= 6 ? 'text-sm' :
    'text-xs';

  // Cargar órdenes iniciales cuando cambie el restaurante seleccionado o el usuario
  useEffect(() => {
    if (userCanAccessKitchen && !selectedRestaurantId) {
      console.log('Usuario no ha seleccionado restaurante');
      setWaitingForUser(false);
      return;
    }
    
    if (selectedRestaurantId && user?.uid) {
      console.log('Restaurante y usuario disponibles');
      setWaitingForUser(false);
    } else if (selectedRestaurantId && !user?.uid) {
      console.log('Restaurante seleccionado pero usuario no disponible aún, esperando...');
      setWaitingForUser(true);
    }
  }, [selectedRestaurantId, userCanAccessKitchen, user?.uid]);

  // Efecto para cargar órdenes iniciales cuando esté disponible el usuario y restaurante
  useEffect(() => {
    if (selectedRestaurantId && user?.uid && !initialOrdersLoaded) {
      console.log('🔄 Cargando órdenes iniciales...');
      loadInitialOrders();
    }
  }, [selectedRestaurantId, user?.uid, initialOrdersLoaded, loadInitialOrders]);



  // Función para actualizar estado de orden
  const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus) => {
    if (!user?.uid) {
      setErrorModal({ open: true, title: 'No autenticado', message: 'Usuario no autenticado' });
      return;
    }

    const currentRestaurantId = selectedRestaurantId;
    if (!currentRestaurantId) {
      setErrorModal({ open: true, title: 'Restaurante no seleccionado', message: 'No hay restaurante seleccionado' });
      return;
    }

    try {
      console.log('🔄 Updating order status:', orderId, 'to', newStatus);
      
      await adminOrdersService.updateOrderStatus(user.uid, currentRestaurantId, orderId, { status: newStatus });
      // La orden se actualizará automáticamente en tiempo real
      setShowOrderModal(false);
      console.log('✅ Order status updated successfully, esperando actualización en tiempo real');
    } catch (error: any) {
      console.error('❌ Error updating order status:', error);
      const message = error instanceof ApiError && error.message ? error.message : (error?.message || 'No se pudo actualizar el estado de la orden');
      const title = error instanceof ApiError && error.status >= 400 && error.status < 500 ? 'No se pudo actualizar' : 'Error del servidor';
      // Cierra el modal de orden y abre el error en el siguiente tick para evitar superposiciones
      setShowOrderModal(false);
      setTimeout(() => {
        setErrorModal({ open: true, title, message, onRetry: () => handleStatusUpdate(orderId, newStatus) });
      }, 0);
    }
  };

  const handleOrderPress = (order: FrontendOrder) => {
    console.log('🧭 Abriendo modal de orden:', order.id);
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Funciones auxiliares
  const getStatusColor = (status: OrderStatus) => {
    return statusColors[status] || '#718096';
  };

  const getStatusText = (status: OrderStatus) => {
    return statusLabels[status] || 'Desconocido';
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min`;
  };

  const formatCreationTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const twoHoursMs = 2 * 60 * 60 * 1000;

    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const timeStr = `${displayHours}:${minutes} ${ampm}`;

    if (diffMs > twoHoursMs) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year} ${timeStr}`;
    }

    return timeStr;
  };

  const getOrdersByStatus = (status: OrderStatus) => {
    // Usar las órdenes agrupadas por estado del hook en tiempo real
    const base = ordersByStatus[status] || [];
    // Mantener orden consistente (más antiguas primero)
    return [...base].sort((a: any, b: any) => {
      const ta = new Date(a?.createdAt as any).getTime();
      const tb = new Date(b?.createdAt as any).getTime();
      return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
    });
  };

  const isNextInQueue = (order: FrontendOrder) => {
    if (order.status !== 'pending') return false;
    const pendingOrders = getOrdersByStatus('pending');
    return pendingOrders.length > 0 && pendingOrders[0].id === order.id;
  };

  // Mostrar loading mientras se valida
  if (loading && realtimeOrders.length === 0) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando órdenes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#e9ecef] flex flex-col">      
      {/* Content */}
      <div className="flex-1 p-2 sm:p-4 overflow-hidden">
        {/* Barra superior con botón Refresh */}
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={handleGlobalRefresh}
            disabled={isRefreshing}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold transition ${isRefreshing ? 'opacity-60 cursor-not-allowed' : 'hover:bg-white'} border-gray-300 text-gray-700 bg-gray-50`}
          >
            <span className="text-lg">↻</span>
            {isRefreshing ? 'Actualizando…' : 'Refrescar'}
          </button>
        </div>
        {!userCanAccessKitchen ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Acceso Denegado</h2>
            <p className="text-gray-500">No tienes permisos para acceder a la cocina</p>
          </div>
        ) : waitingForUser ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-gray-600">🔄 Cargando información del usuario...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 h-full">
            {/* Columna Creadas */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Creadas ({getOrdersByStatus('created').length})
                </h3>
              </div>
              <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 flex-1 overflow-y-auto">
                {getOrdersByStatus('created').map(realtimeOrder => {
                  const order = convertRealtimeOrderToFrontend(realtimeOrder);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      isPriority={false}
                      isNextInQueue={false}
                      onPress={() => handleOrderPress(order)}
                      getStatusColor={getStatusColor}
                      formatTime={formatTime}
                      formatCreationTime={formatCreationTime}
                      formatPrice={formatPrice}
                      canViewAmounts={canViewAmounts}
                    />
                  );
                })}
                {getOrdersByStatus('created').length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
                    No hay órdenes creadas
                  </div>
                )}
              </div>
            </div>

            {/* Columna Pendientes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Pendientes ({getOrdersByStatus('pending').length})
                </h3>
              </div>
              <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 flex-1 overflow-y-auto">
                {getOrdersByStatus('pending').map(realtimeOrder => {
                  const order = convertRealtimeOrderToFrontend(realtimeOrder);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      isPriority={false}
                      isNextInQueue={isNextInQueue(order)}
                      onPress={() => handleOrderPress(order)}
                      getStatusColor={getStatusColor}
                      formatTime={formatTime}
                      formatCreationTime={formatCreationTime}
                      formatPrice={formatPrice}
                      canViewAmounts={canViewAmounts}
                    />
                  );
                })}
                {getOrdersByStatus('pending').length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
                    No hay órdenes pendientes
                  </div>
                )}
              </div>
            </div>

            {/* Columna Preparando */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Preparando ({getOrdersByStatus('preparing').length})
                </h3>
              </div>
              <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 flex-1 overflow-y-auto">
                {getOrdersByStatus('preparing').map(realtimeOrder => {
                  const order = convertRealtimeOrderToFrontend(realtimeOrder);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      isPriority={false}
                      isNextInQueue={false}
                      onPress={() => handleOrderPress(order)}
                      getStatusColor={getStatusColor}
                      formatTime={formatTime}
                      formatCreationTime={formatCreationTime}
                      formatPrice={formatPrice}
                      canViewAmounts={canViewAmounts}
                    />
                  );
                })}
                {getOrdersByStatus('preparing').length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
                    No hay órdenes preparándose
                  </div>
                )}
              </div>
            </div>

            {/* Columna Listos */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 bg-green-50 flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Listos ({getOrdersByStatus('ready').length})
                </h3>
              </div>
              <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 flex-1 overflow-y-auto">
                {getOrdersByStatus('ready').map(realtimeOrder => {
                  const order = convertRealtimeOrderToFrontend(realtimeOrder);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      isPriority={false}
                      isNextInQueue={false}
                      onPress={() => handleOrderPress(order)}
                      getStatusColor={getStatusColor}
                      formatTime={formatTime}
                      formatCreationTime={formatCreationTime}
                      formatPrice={formatPrice}
                      canViewAmounts={canViewAmounts}
                    />
                  );
                })}
                {getOrdersByStatus('ready').length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
                    No hay órdenes listas
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Modal de detalle de orden (portal para evitar clipping) */}
      {!errorModal.open && showOrderModal && selectedOrder && createPortal((
        <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowOrderModal(false)} />
          <div className="relative bg-white rounded-none sm:rounded-3xl shadow-2xl w-full h-full sm:h-auto sm:max-h-[92vh] max-w-none sm:max-w-3xl lg:max-w-5xl border border-gray-200 overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#2C4358]">
                  {selectedOrder.tableNumber === 0
                    ? `#${getOrderShortCode(selectedOrder.id)}`
                    : `Mesa ${selectedOrder.tableNumber}`}
                </h3>
                <p className="text-sm text-gray-500">Estado: {getStatusText(selectedOrder.status)}</p>
              </div>
              <div className="flex items-center gap-2">
                {selectedOrder.status !== 'cancelled' && (
                  <button
                    type="button"
                    onClick={() => setConfirmCancel({ open: true, orderId: selectedOrder.id })}
                    className="h-14 sm:h-16 px-6 bg-red-50 text-red-700 border border-red-200 rounded-2xl font-bold text-lg hover:bg-red-100"
                  >
                    Cancelar orden
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowOrderModal(false)}
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items.map((it, idx) => (
                    <div key={idx} className={`flex justify-between ${modalItemsTextClass} leading-snug`}>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">x{it.quantity}</span>
                        <span className="text-gray-800">{it.name}</span>
                        {(it as any).size && (
                          <span className="ml-1 text-xs sm:text-sm text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{(it as any).size}</span>
                        )}
                        {(it as any).specialInstructions && (
                          <span className="ml-1 text-xs sm:text-sm text-gray-500 italic">
                            {(it as any).specialInstructions}
                          </span>
                        )}
                      </div>
                      {canViewAmounts && (
                        <span className="text-gray-700">${(typeof it.price === 'number' ? it.price : Number(it.price || 0)).toFixed(2)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {selectedOrder.specialNotes && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Notas especiales</h4>
                  <p className={`${modalItemsTextClass} text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 leading-snug`}>{selectedOrder.specialNotes}</p>
                </div>
              )}
              <div className={`flex items-center justify-between ${modalMetaTextClass} text-gray-600`}>
                <span>Creada: {formatCreationTime(selectedOrder.createdAt)}</span>
                {canViewAmounts && (selectedOrder.status === 'ready' || selectedOrder.status === 'delivered') && (
                  <span className="font-semibold text-green-700">Total: {formatPrice(selectedOrder.totalAmount)}</span>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowOrderModal(false)}
                className="h-16 sm:h-20 px-7 sm:px-9 bg-white border-2 border-gray-200 text-[#2C4358] rounded-2xl font-bold text-lg sm:text-xl hover:bg-gray-100"
              >
                Cerrar
              </button>
              {selectedOrder.status === 'created' && (
                <button
                  type="button"
                  onClick={() => handleStatusUpdate(selectedOrder.id, 'pending')}
                  className="h-16 sm:h-20 px-7 sm:px-9 bg-blue-600 text-white rounded-2xl font-bold text-lg sm:text-xl hover:bg-blue-700"
                >
                  Confirmar (Pasar a Pendiente)
                </button>
              )}
              {selectedOrder.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => handleStatusUpdate(selectedOrder.id, 'preparing')}
                  className="h-16 sm:h-20 px-7 sm:px-9 bg-blue-600 text-white rounded-2xl font-bold text-lg sm:text-xl hover:bg-blue-700"
                >
                  Marcar como Preparando
                </button>
              )}
              {selectedOrder.status === 'preparing' && (
                <button
                  type="button"
                  onClick={() => handleStatusUpdate(selectedOrder.id, 'ready')}
                  className="h-16 sm:h-20 px-7 sm:px-9 bg-green-600 text-white rounded-2xl font-bold text-lg sm:text-xl hover:bg-green-700"
                >
                  Marcar como Listo
                </button>
              )}
              {selectedOrder.status === 'ready' && (
                <button
                  type="button"
                  onClick={() => handleStatusUpdate(selectedOrder.id, 'delivered')}
                  className="h-16 sm:h-20 px-7 sm:px-9 bg-purple-600 text-white rounded-2xl font-bold text-lg sm:text-xl hover:bg-purple-700"
                >
                  Marcar como Entregado
                </button>
              )}
            </div>
          </div>
        </div>
      ), document.body)}
      {/* Confirmación para cancelar */}
      {createPortal(
        <ConfirmModal
          isOpen={confirmCancel.open}
          title="Cancelar orden"
          message="¿Seguro que deseas cancelar esta orden? Esta acción no se puede deshacer."
          confirmText="Sí, cancelar"
          onConfirm={() => {
            if (confirmCancel.orderId) {
              handleStatusUpdate(confirmCancel.orderId, 'cancelled');
            }
            setConfirmCancel({ open: false });
          }}
          onCancel={() => setConfirmCancel({ open: false })}
        />,
        document.body
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} onRetry={errorModal.onRetry} />

      <style>{`
        .order-card {
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .order-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .order-card.priority {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .order-card.next-in-queue {
          border-color: #10b981;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
        }

        .order-card.old-order {
          border-color: #f59e0b;
          background-color: #fffbeb;
        }

        .order-status-bar {
          height: 3px;
          width: 100%;
        }

        .order-header {
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 1px solid #f3f4f6;
        }

        .order-info {
          flex: 1;
          min-width: 0;
        }

        .order-number {
          font-weight: 600;
          font-size: 14px;
          color: #1f2937;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .order-time {
          font-size: 11px;
          color: #6b7280;
        }

        .order-status {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 3px 6px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
        }

        .status-icon {
          font-size: 12px;
        }

        .status-text {
          color: white;
        }

        .order-items {
          padding: 8px 12px;
          space-y: 6px;
        }

        .order-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 3px 0;
        }

        .item-quantity {
          font-weight: 600;
          color: #6b7280;
          min-width: 20px;
          font-size: 12px;
        }

        .item-name {
          flex: 1;
          margin: 0 6px;
          font-size: 12px;
        }

        .item-price {
          font-weight: 500;
          color: #374151;
          font-size: 12px;
        }

        .order-notes {
          padding: 0 12px 8px;
        }

        .notes-label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 3px;
        }

        .notes-text {
          font-size: 11px;
          color: #374151;
          background-color: #f9fafb;
          padding: 6px;
          border-radius: 4px;
        }

        .order-footer {
          padding: 8px 12px;
          border-top: 1px solid #f3f4f6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .order-total {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .total-label {
          font-size: 11px;
          color: #6b7280;
        }

        .total-amount {
          font-weight: 600;
          color: #059669;
          font-size: 12px;
        }

        .order-creation-time {
          font-size: 10px;
          color: #9ca3af;
        }

        .old-order-warning {
          background-color: #fef3c7;
          color: #92400e;
          padding: 6px 8px;
          font-size: 11px;
          font-weight: 600;
          text-align: center;
        }

        .order-items-simple {
          padding: 0 12px 8px;
        }

        /* Responsive adjustments */
        @media (min-width: 640px) {
          .order-card {
            border-radius: 12px;
          }

          .order-status-bar {
            height: 4px;
          }

          .order-header {
            padding: 12px;
          }

          .order-number {
            font-size: 16px;
            margin-bottom: 4px;
          }

          .order-time {
            font-size: 12px;
          }

          .order-status {
            gap: 4px;
            padding: 4px 8px;
            font-size: 12px;
          }

          .status-icon {
            font-size: 14px;
          }

          .order-items {
            padding: 12px;
            space-y: 8px;
          }

          .order-item {
            padding: 4px 0;
          }

          .item-quantity {
            min-width: 24px;
            font-size: 14px;
          }

          .item-name {
            margin: 0 8px;
            font-size: 14px;
          }

          .item-price {
            font-size: 14px;
          }

          .order-notes {
            padding: 0 12px 12px;
          }

          .notes-label {
            font-size: 12px;
            margin-bottom: 4px;
          }

          .notes-text {
            font-size: 12px;
            padding: 8px;
            border-radius: 6px;
          }

          .order-footer {
            padding: 12px;
          }

          .total-label {
            font-size: 12px;
          }

          .total-amount {
            font-size: 14px;
          }

          .order-creation-time {
            font-size: 11px;
          }

          .old-order-warning {
            padding: 8px 12px;
            font-size: 12px;
          }

          .order-items-simple {
            padding: 0 12px 12px;
          }
        }
      `}</style>
    </div>
  );
}
